/**
 * RemovableTagChipContainer — binds the tag's name into its remove handler.
 *
 * This lived inside `PlayerCardTagsView` as a second component, and its own
 * note explained why it was a component at all: "so the name-bound remove
 * handler is a stable reference per chip (`react-perf/jsx-no-new-function-as-
 * prop`), which also keeps the memoized pill from re-rendering on every parent
 * render." That is a row Container's job, not a reason to nest a component in a
 * View file (SE-8019).
 * @module components/organisms/custom/ui/RemovableTagChip/RemovableTagChipContainer
 */

import React, { memo, useCallback } from "react";

import RemovableTagChipView from "./RemovableTagChipView";

/** Props for {@link RemovableTagChipContainer}. */
export interface RemovableTagChipContainerProps {
  /** The tag's display name — the chip's label and its identity. */
  readonly name: string;
  /** Removal handler, called with the tag name. */
  readonly onRemoveTag: (tagName: string) => void;
  /** Suppresses a drag starting from a tag interaction. */
  readonly onPreventDrag?: () => void;
}

/**
 * Binds the tag's identity and renders the chip.
 * @param root0 - Chip props
 * @param root0.name - The tag's display name
 * @param root0.onRemoveTag - Removal handler, called with the tag name
 * @param root0.onPreventDrag - Drag suppression on tag interaction
 * @returns The bound chip.
 */
const RemovableTagChipContainer = ({
  name,
  onRemoveTag,
  onPreventDrag,
}: RemovableTagChipContainerProps) => {
  const handleRemove = useCallback(
    () => onRemoveTag(name),
    [onRemoveTag, name]
  );

  return (
    <RemovableTagChipView
      name={name}
      onRemove={handleRemove}
      onPreventDrag={onPreventDrag}
    />
  );
};

RemovableTagChipContainer.displayName = "RemovableTagChipContainer";

export default memo(RemovableTagChipContainer);
